export type NavigationIntentReason = 'emblem-redirect';

interface NavigationIntentRecord {
  createdAt: number;
  reason: NavigationIntentReason;
  sessionId?: string;
}

const STORAGE_KEY = 'safepassage:intentional-navigation';
const INTENT_TTL_MS = 10 * 60 * 1000;

function readIntent(): NavigationIntentRecord | null {
  try {
    const rawValue = sessionStorage.getItem(STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue) as Partial<NavigationIntentRecord>;
    if (
      typeof parsed.createdAt !== 'number' ||
      typeof parsed.reason !== 'string'
    ) {
      clearIntentionalNavigation();
      return null;
    }

    return {
      createdAt: parsed.createdAt,
      reason: parsed.reason as NavigationIntentReason,
      sessionId:
        typeof parsed.sessionId === 'string' ? parsed.sessionId : undefined,
    };
  } catch {
    clearIntentionalNavigation();
    return null;
  }
}

export function markIntentionalNavigation(
  reason: NavigationIntentReason,
  sessionId?: string
): void {
  const intent: NavigationIntentRecord = {
    createdAt: Date.now(),
    reason,
  };

  if (sessionId) {
    intent.sessionId = sessionId;
  }

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(intent));
  } catch {
    // Ignore storage failures and fall back to default abandonment behavior.
  }
}

export function hasActiveIntentionalNavigation(sessionId?: string): boolean {
  const intent = readIntent();
  if (!intent) {
    return false;
  }

  if (Date.now() - intent.createdAt > INTENT_TTL_MS) {
    clearIntentionalNavigation();
    return false;
  }

  if (sessionId && intent.sessionId && intent.sessionId !== sessionId) {
    return false;
  }

  return true;
}

export function clearIntentionalNavigation(sessionId?: string): void {
  if (sessionId) {
    const intent = readIntent();
    if (intent?.sessionId && intent.sessionId !== sessionId) {
      return;
    }
  }

  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}
